import {
  Award,
  Calendar,
  CheckCircle2,
  ClipboardCheck,
  FileSignature,
  GraduationCap,
  IdCard,
  Lock,
  PauseCircle,
  Shield,
} from 'lucide-react';
import { Progress } from '../ui/progress';
import WorkerPremiumCard from './WorkerPremiumCard';

const STEP_ICONS = {
  shield: Shield,
  'id-card': IdCard,
  calendar: Calendar,
  'clipboard-check': ClipboardCheck,
  'graduation-cap': GraduationCap,
  award: Award,
  'file-signature': FileSignature,
};

const STATUS_STYLES = {
  completed: 'bg-green-100 text-green-700 ring-green-200',
  in_progress: 'bg-primary/10 text-primary ring-primary/20',
  available: 'bg-slate-100 text-slate-700 ring-slate-200',
  awaiting_review: 'bg-blue-100 text-blue-700 ring-blue-200',
  paused: 'bg-purple-100 text-purple-700 ring-purple-200',
  locked: 'bg-slate-50 text-slate-400 ring-slate-100',
};

function StepIcon({ icon, className }) {
  const Icon = STEP_ICONS[icon] || Shield;
  return <Icon className={className} />;
}

export default function OnboardingWizardShell({
  wizard,
  children,
  onStepSelect,
  nextActionSlot = null,
  stepContext = null,
  hidePostInterviewSteps = false,
  hideStepContent = false,
  hideStepIntro = false,
}) {
  if (!wizard?.enabled) return children;

  const allSteps = wizard.steps || [];
  const interviewComplete = allSteps.some(
    (step) => step.id === 'interview' && step.status === 'completed'
  );
  // Recruitment approval gates Care Certificate and agreements, NOT identity
  // uploads or existing mandatory-training certificates. Keep pre-employment
  // tasks visible even while a declaration or interview awaits a manager.
  const steps = hidePostInterviewSteps
    ? allSteps.filter((step) => (
        !['care_certificate', 'agreements'].includes(step.id)
        && (step.id !== 'final_compliance_review' || interviewComplete)
      ))
    : allSteps;
  const currentStep = steps.find((s) => s.is_current) || steps.find((s) => s.id === wizard.current_step_id);
  const rawProgress = wizard.progress || { percentage: 0, completed_steps: 0, total_steps: allSteps.length };
  const visibleStepNumber = Math.max(steps.findIndex((s) => s.is_current) + 1, 1);
  // Progress reflects GENUINE completed steps out of the full set (fixed
  // denominator) — never a visible-position estimate. This keeps the bar
  // monotonic (never drops when hidden steps re-appear) and prevents showing
  // 100% while the current step is still incomplete, or 0% when later steps
  // are already completed.
  const genuineCompleted = allSteps.filter((s) => s.status === 'completed').length;
  const genuinePercentage = typeof rawProgress.percentage === 'number'
    ? rawProgress.percentage
    : (allSteps.length ? Math.round((genuineCompleted / allSteps.length) * 100) : 0);
  const progress = steps.length < allSteps.length
    ? {
        ...rawProgress,
        total_steps: allSteps.length,
        current_step_number: visibleStepNumber,
        percentage: genuinePercentage,
      }
    : { ...rawProgress, total_steps: allSteps.length, percentage: genuinePercentage };
  const isPaused = currentStep?.is_paused;
  const StepIconComponent = currentStep ? STEP_ICONS[currentStep.icon] || Shield : Shield;
  const panelTitle = stepContext?.title || currentStep?.title;
  const panelSubtitle = stepContext?.subtitle || currentStep?.subtitle;
  const panelDescription = stepContext?.description || currentStep?.description;

  return (
    <div className="space-y-4" data-testid="onboarding-wizard-shell">
      <WorkerPremiumCard className="p-0 overflow-hidden" data-testid="onboarding-wizard-header">
        <div className="border-b border-black/[0.04] bg-gradient-to-br from-primary/[0.05] to-white px-5 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-primary/70">
              Your onboarding
            </p>
            <h2 className="mt-1 font-heading text-xl font-bold text-[#17324D]">
              {panelTitle || 'One step at a time'}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {genuineCompleted} of {allSteps.length} stages complete
              {' · '}
              {progress.percentage}% overall
            </p>
          </div>
          <Progress value={progress.percentage} className="mt-4 h-2 rounded-full" />
        </div>

        <div className="border-t border-black/[0.04] px-4 py-3 sm:hidden" data-testid="wizard-mobile-summary">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 truncate font-medium text-slate-700">
              Current: {panelTitle || currentStep?.title || 'Onboarding'}
            </span>
            <span className="shrink-0 text-xs font-semibold text-primary">
              {genuineCompleted}/{allSteps.length} done
            </span>
          </div>
        </div>

        <div className="hidden max-w-full gap-2 overflow-x-auto overscroll-x-contain px-4 py-3 scrollbar-thin sm:flex">
          {steps.map((step, index) => {
            const statusClass = STATUS_STYLES[step.status] || STATUS_STYLES.locked;
            // A worker may open other server-authorised tasks without waiting
            // for the current manager-owned review to finish. Locked and
            // awaiting-review steps remain non-actionable.
            const clickable = Boolean(onStepSelect)
              && ['completed', 'available', 'in_progress'].includes(step.status);
            return (
              <button
                key={step.id}
                type="button"
                disabled={!clickable}
                onClick={() => clickable && onStepSelect(step.id)}
                className={`flex min-w-[7.5rem] max-w-[7.5rem] shrink-0 flex-col items-center gap-1.5 rounded-xl px-2 py-2 text-center transition ${
                  step.is_current ? 'bg-primary/[0.06] ring-1 ring-primary/20' : 'hover:bg-slate-50'
                } ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                data-testid={`wizard-step-pill-${step.id}`}
              >
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-full ring-1 ${statusClass}`}
                >
                  {step.status === 'completed' ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : step.status === 'locked' ? (
                    <Lock className="h-3.5 w-3.5" />
                  ) : step.is_current && isPaused ? (
                    <PauseCircle className="h-4 w-4" />
                  ) : (
                    <StepIcon icon={step.icon} className="h-4 w-4" />
                  )}
                </span>
                <span className="text-[10px] font-semibold leading-tight text-slate-700 break-words">
                  {index + 1}. {step.title}
                </span>
              </button>
            );
          })}
        </div>
      </WorkerPremiumCard>

      {currentStep && !hideStepIntro ? (
        <WorkerPremiumCard accent="teal" data-testid={`wizard-step-panel-${currentStep.id}`}>
          <div className="flex items-start gap-3.5">
            <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/15 sm:flex">
              <StepIconComponent className="h-5 w-5 text-primary" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="hidden text-[11px] font-semibold uppercase tracking-[0.12em] text-primary/70 sm:block">
                {panelSubtitle}
              </p>
              <h3 className="mt-0.5 hidden font-heading text-lg font-bold text-[#17324D] sm:block">{panelTitle}</h3>
              <p className="text-sm leading-relaxed text-slate-600 sm:mt-1.5">{panelDescription}</p>
            </div>
          </div>

          {isPaused && currentStep.pause_message ? (
            <div
              className="mt-4 rounded-xl border border-purple-200/80 bg-purple-50/80 px-4 py-3 text-sm text-purple-900"
              data-testid="wizard-pause-message"
            >
              <p className="font-medium">We&apos;re reviewing this step</p>
              <p className="mt-1 text-purple-800/90">{currentStep.pause_message}</p>
            </div>
          ) : null}

          {currentStep.playbook ? (
            <div className="mt-4 rounded-xl border border-[#E4E8EB] bg-slate-50/80 p-4" data-testid="wizard-playbook">
              <p className="text-sm font-semibold text-[#17324D]">{currentStep.playbook.headline}</p>
              <ol className="mt-3 space-y-2">
                {(currentStep.playbook.steps || []).map((item) => (
                  <li key={item.order} className="flex gap-3 text-sm text-slate-700">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                      {item.order}
                    </span>
                    <span>
                      <span className="font-medium text-slate-900">{item.title}</span>
                      <span className="block text-slate-600">{item.detail}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </WorkerPremiumCard>
      ) : null}

      {nextActionSlot}

      {!hideStepContent && !isPaused ? (
        <div data-testid="wizard-step-content">{children}</div>
      ) : null}
    </div>
  );
}
